/**
 * BomTreeExplore — "explore the whole BOM hierarchy" mode.
 *
 * Loads on mount with every BOM in `manufacturing.boms` and lets the
 * operator pick a root product. Each node renders with the Onshape (i)
 * chip, a generic drawing PDF chip, and a dedicated WI (work-instruction)
 * chip in the top-right. Click chevron to expand sub-assemblies; click (i)
 * to open the matching Onshape document.
 *
 * Previously lived in BomGraph.tsx. The tree-build + render logic is
 * preserved verbatim — only the SVG canvas, pan/zoom, and expand-state
 * machinery now lives in the shared BomTreeCore primitive.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { Chip, MenuItem, Paper, Select, Stack, Tooltip, Typography } from '@mui/material';
import {
  BomLine,
  BomWithLines,
  ManufacturingProduct,
  OnshapeDocument,
  PartsByCodeEntry,
  PartsByCodeResponse,
} from '../../types/manufacturing';
import { fetchManufacturingDrawingBlob } from './Manufacturing.queries';
import {
  BomTreeCore,
  BomTreeCoreHandle,
  BomTreeToolbar,
  type BomTreeRenderNodeArgs,
} from './BomTreeCore';

interface Props {
  boms: BomWithLines[];
  products: ManufacturingProduct[];
  onshapeDocuments?: OnshapeDocument[];
  // default_code -> Onshape part + drawing/PDF. Resolved upstream via
  // /api/manufacturing/onshape/parts-by-code (exact equality on
  // part_number == default_code, same join the drift view uses). Replaces
  // the old substring-on-document-name heuristic. Undefined while loading.
  partsByCode?: PartsByCodeResponse;
  loading: boolean;
}

interface AssemblyNode {
  // Path-derived unique key. Format: `root:productId` for the root,
  // `${parentKey}/${productId}-${lineIdx}` for everything else. Encoding
  // the full path means two occurrences of the same product under
  // different parents get distinct keys — necessary so React reconciles
  // them as separate nodes and expand-state doesn't leak between them.
  key: string;
  productId: string;
  bomId: string | null; // null = leaf (no BOM)
  qty: number;
  partsCount: number; // count of non-assembly direct children
  partsQtyTotal: number;
  children: AssemblyNode[];
}

const MAX_DEPTH = 8;
const NODE_W = 230;
const NODE_H = 64;

// Depth-based palette — clean, not tied to MUI theme.
const DEPTH_COLORS = ['#1e40af', '#0891b2', '#15803d', '#b45309', '#a21caf', '#be123c', '#0f766e'];

function buildAssemblyTree(
  bom: BomWithLines | undefined,
  productId: string,
  qty: number,
  bomsByProductId: Map<string, BomWithLines>,
  visited: Set<string>,
  depth: number,
  parentKey: string | null,
  lineIdx: number
): AssemblyNode {
  const key = parentKey === null ? `root:${productId}` : `${parentKey}/${productId}-${lineIdx}`;
  let partsCount = 0;
  let partsQtyTotal = 0;
  const assemblyChildren: AssemblyNode[] = [];

  if (bom && depth < MAX_DEPTH && !visited.has(productId)) {
    const next = new Set(visited);
    next.add(productId);
    bom.bom_lines.forEach((line: BomLine, idx) => {
      const childBom = bomsByProductId.get(line.product_id);
      if (childBom) {
        assemblyChildren.push(
          buildAssemblyTree(
            childBom,
            line.product_id,
            line.product_qty,
            bomsByProductId,
            next,
            depth + 1,
            key,
            idx
          )
        );
      } else {
        partsCount += 1;
        partsQtyTotal += line.product_qty || 0;
      }
    });
  }

  return {
    key,
    productId,
    bomId: bom?.id ?? null,
    qty,
    partsCount,
    partsQtyTotal,
    children: assemblyChildren,
  };
}

/**
 * Look up a product's Onshape match via the proper part-number join
 * (default_code -> onshape.parts.part_number). Returns null when there's
 * no usable default_code or no match in the lookup. This is the only join
 * the backend exposes for the BomTree chips — the previous substring-on-
 * document-name hack is gone.
 */
function findOnshapeEntry(
  defaultCode: string | null | undefined,
  partsByCode: PartsByCodeResponse | undefined
): PartsByCodeEntry | null {
  if (!partsByCode || !defaultCode) return null;
  const code = defaultCode.trim();
  if (!code) return null;
  return partsByCode[code] ?? null;
}

/**
 * Open a backend-proxied Onshape drawing PDF in a new browser tab.
 *
 * The drawing endpoint is JWT-gated and lives on the API origin
 * (`VITE_API_URL`), which is NOT the SPA origin. So we FETCH the PDF through
 * the authenticated axios client (correct base + bearer token) and open the
 * resulting object URL — we must NOT `window.open` a bare `/api/...` path, as
 * that navigates the SPA origin with no auth and lands on the dashboard root
 * instead of the PDF (the #1067 follow-up bug this replaces).
 *
 * The blank tab is opened synchronously inside the click gesture so the popup
 * blocker lets it through; we navigate it to the object URL once the PDF
 * resolves. `noopener` is intentionally omitted (it would null the handle); we
 * sever `opener` manually instead.
 */
async function openProxyDrawingPdf(elementId: string, label: string): Promise<void> {
  const tab = window.open('', '_blank');
  if (tab) {
    tab.opener = null;
    // textContent, not document.write: document.write parses HTML, so it would
    // be an XSS sink if `label` ever became caller/server-controlled.
    if (tab.document.body) tab.document.body.textContent = `Loading ${label}…`;
  }
  try {
    const blob = await fetchManufacturingDrawingBlob(elementId);
    const url = URL.createObjectURL(blob);
    if (tab) {
      // Arm the revoke BEFORE navigating: if `location.href` throws (e.g. the
      // user closed the tab mid-fetch), the timer still reclaims the object URL
      // so the PDF blob it pins in memory isn't leaked. The PDF viewer needs
      // the URL to stay live while it loads, hence the delay.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      tab.location.href = url;
    } else {
      // Popup blocked — fall back to a download in the current tab. The click
      // queues the download synchronously, so the URL can be revoked at once.
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  } catch {
    // Best-effort error message. The tab may have been closed mid-fetch; reading
    // `tab.closed` is always safe, and short-circuits before touching the
    // (possibly disconnected) document.
    if (tab && !tab.closed && tab.document.body) {
      tab.document.body.textContent = `Could not load ${label}. Please try again.`;
    }
  }
}

interface NodeRectProps {
  args: BomTreeRenderNodeArgs<AssemblyNode>;
  productById: Map<string, ManufacturingProduct>;
  // Reserved for future per-document chrome (e.g. document thumbnail or
  // multi-revision badge). Kept on the props so the wrapper can keep passing
  // it without churn even though NodeRect no longer needs it for matching.
  onshapeDocs: OnshapeDocument[] | undefined;
  partsByCode: PartsByCodeResponse | undefined;
}

function NodeRect({ args, productById, partsByCode }: NodeRectProps) {
  const { node, pos, depth, isExpanded, hasChildren, onToggle } = args;
  const color = DEPTH_COLORS[depth % DEPTH_COLORS.length];
  const product = productById.get(node.productId);
  const label = product?.default_code ?? product?.name ?? node.productId.slice(0, 8);
  const fullName = product?.name ?? label;
  const onshapeEntry = findOnshapeEntry(product?.default_code ?? null, partsByCode);
  const onshapeHref = onshapeEntry?.onshape_url ?? null;
  // PDF chip. Prefer the backend-proxied drawing element (no Onshape login
  // required, streams a translated PDF) — that endpoint is JWT-gated, so it is
  // FETCHED via openProxyDrawingPdf, never window.open'd. Fall back to the
  // legacy absolute cad.onshape.com BLOB href, which is external and opens
  // directly. `pdfElementId` wins when both exist.
  const pdfElementId = onshapeEntry?.drawing_element_id ?? null;
  const pdfFallbackHref = onshapeEntry?.pdf_href ?? null;
  const hasPdf = Boolean(pdfElementId || pdfFallbackHref);
  const pdfTooltip = pdfElementId
    ? `Open work-instruction drawing (${onshapeEntry?.part_number}) as PDF`
    : pdfFallbackHref
      ? 'Open work-instruction PDF (legacy BLOB)'
      : 'No work-instruction drawing or PDF';
  // Dedicated WI chip: opens the document's work-instruction drawing (tab name
  // flagged "WI"), distinct from the generic PDF chip above so docs that have a
  // drawing but no WI keep their PDF access. Greyed when there's no WI. The
  // backend serves the latest from the main workspace (no released-vs-draft
  // state), so the tooltip flags that it may be work-in-progress. Like the PDF
  // proxy, this drawing is auth-gated and so is fetched, not window.open'd.
  const wiElementId = onshapeEntry?.wi_drawing_element_id ?? null;
  const wiTooltip = wiElementId
    ? `Open work instruction (${
        onshapeEntry?.wi_drawing_name ?? 'WI'
      }) — latest from Onshape, may be work-in-progress`
    : 'No work instruction';
  const nx = pos.x;
  const ny = pos.y;

  return (
    <g>
      <rect
        x={nx}
        y={ny}
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill="#ffffff"
        stroke={color}
        strokeWidth={2}
      />
      {/* Color stripe on the left edge for depth feedback */}
      <rect x={nx} y={ny} width={6} height={NODE_H} rx={3} fill={color} />

      <text x={nx + 14} y={ny + 18} fontSize={13} fontWeight={700} fill="#0f172a">
        {label.length > 26 ? `${label.slice(0, 26)}…` : label}
      </text>
      {fullName !== label && (
        <text x={nx + 14} y={ny + 33} fontSize={10} fill="#475569">
          {fullName.length > 32 ? `${fullName.slice(0, 32)}…` : fullName}
        </text>
      )}
      <text x={nx + 14} y={ny + 51} fontSize={11} fill="#64748b">
        ×{node.qty.toLocaleString()}
        {node.partsCount > 0 && `  •  ${node.partsCount} part${node.partsCount === 1 ? '' : 's'}`}
        {hasChildren && `  •  ${node.children.length} sub`}
      </text>

      {/* Action cluster on the top-right of the node: (i) · PDF · WI · chevron */}
      <g transform={`translate(${nx + NODE_W - 112}, ${ny + 8})`}>
        <Tooltip
          title={
            onshapeHref
              ? `Open ${onshapeEntry?.part_number} in Onshape (requires Onshape login)`
              : 'No Onshape part match'
          }
        >
          <g
            data-testid="bom-node-onshape-chip"
            style={{
              cursor: onshapeHref ? 'pointer' : 'not-allowed',
              opacity: onshapeHref ? 1 : 0.35,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onshapeHref) window.open(onshapeHref, '_blank', 'noopener,noreferrer');
            }}
          >
            <circle cx={11} cy={11} r={11} fill={onshapeHref ? color : '#cbd5e1'} />
            <text
              x={11}
              y={15}
              fontSize={12}
              fontWeight={700}
              fill="#ffffff"
              textAnchor="middle"
              fontFamily="serif"
              fontStyle="italic"
            >
              i
            </text>
          </g>
        </Tooltip>
        <Tooltip title={pdfTooltip}>
          <g
            data-testid="bom-node-pdf-chip"
            transform="translate(28,0)"
            style={{
              cursor: hasPdf ? 'pointer' : 'not-allowed',
              opacity: hasPdf ? 1 : 0.35,
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Proxied drawing element → authenticated fetch + open the blob.
              // Legacy absolute BLOB href → external, open directly.
              if (pdfElementId) {
                void openProxyDrawingPdf(pdfElementId, 'work-instruction drawing');
              } else if (pdfFallbackHref) {
                window.open(pdfFallbackHref, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <circle cx={11} cy={11} r={11} fill={hasPdf ? '#dc2626' : '#cbd5e1'} />
            <text x={11} y={14} fontSize={7} fontWeight={800} fill="#ffffff" textAnchor="middle">
              PDF
            </text>
          </g>
        </Tooltip>
        <Tooltip title={wiTooltip}>
          <g
            data-testid="bom-node-wi-chip"
            transform="translate(56,0)"
            style={{
              cursor: wiElementId ? 'pointer' : 'not-allowed',
              opacity: wiElementId ? 1 : 0.35,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (wiElementId) void openProxyDrawingPdf(wiElementId, 'work instruction');
            }}
          >
            <circle cx={11} cy={11} r={11} fill={wiElementId ? '#7c3aed' : '#cbd5e1'} />
            <text x={11} y={14} fontSize={7} fontWeight={800} fill="#ffffff" textAnchor="middle">
              WI
            </text>
          </g>
        </Tooltip>
        {hasChildren && (
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand sub-assemblies'}>
            <g
              style={{ cursor: 'pointer' }}
              transform="translate(84,0)"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <circle
                cx={11}
                cy={11}
                r={11}
                fill={color}
                fillOpacity={0.15}
                stroke={color}
                strokeWidth={1}
              />
              {isExpanded ? (
                <path
                  d="M 6 13 L 11 8 L 16 13"
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M 6 9 L 11 14 L 16 9"
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </g>
          </Tooltip>
        )}
      </g>
    </g>
  );
}

export function BomTreeExplore({ boms, products, onshapeDocuments, partsByCode, loading }: Props) {
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  // Keep the deepest BOM per product (some products have multiple). Tie-break
  // by bom_lines.length to favour the most expressive variant. Same rule as
  // the previous tree implementation — see project_bom_multiple_per_product.
  const bomsByProductId = useMemo(() => {
    const map = new Map<string, BomWithLines>();
    for (const b of boms) {
      const existing = map.get(b.product_id);
      if (!existing || b.bom_lines.length > existing.bom_lines.length) {
        map.set(b.product_id, b);
      }
    }
    return map;
  }, [boms]);

  const bomsById = useMemo(() => new Map(boms.map((b) => [b.id, b])), [boms]);

  // Root candidates: BOMs whose product is *not* referenced as a line in any
  // other BOM. Falls back to "BOMs with most lines" if the heuristic excludes
  // everything (cycle / disconnected graph).
  const rootCandidates = useMemo(() => {
    const referencedProductIds = new Set<string>();
    for (const b of boms) {
      for (const line of b.bom_lines) referencedProductIds.add(line.product_id);
    }
    const tops = boms
      .filter((b) => !referencedProductIds.has(b.product_id))
      .sort((a, b) => b.bom_lines.length - a.bom_lines.length);
    if (tops.length > 0) return tops.slice(0, 100);
    return [...boms].sort((a, b) => b.bom_lines.length - a.bom_lines.length).slice(0, 100);
  }, [boms]);

  const [rootBomId, setRootBomId] = useState<string>('');
  const effectiveRootBomId = rootBomId || rootCandidates[0]?.id || '';
  const rootBom = effectiveRootBomId ? bomsById.get(effectiveRootBomId) : undefined;

  const tree = useMemo(() => {
    if (!rootBom) return null;
    return buildAssemblyTree(
      rootBom,
      rootBom.product_id,
      1,
      bomsByProductId,
      new Set(),
      0,
      null,
      0
    );
  }, [rootBom, bomsByProductId]);

  const handleRef = useRef<BomTreeCoreHandle>(null);

  // Stable references for the BomTreeCore render-props API. Without these,
  // every parent re-render (loading toggle, syncStatus update, etc.) hands
  // BomTreeCoreInner fresh function identities and invalidates its
  // allKeys / layout memos — a full tree walk per page rerender. See PR
  // review on #749.
  const childrenOf = useCallback((n: AssemblyNode) => n.children, []);
  const keyOf = useCallback((n: AssemblyNode) => n.key, []);
  const renderNode = useCallback(
    (args: BomTreeRenderNodeArgs<AssemblyNode>) => (
      <NodeRect
        args={args}
        productById={productById}
        onshapeDocs={onshapeDocuments}
        partsByCode={partsByCode}
      />
    ),
    [productById, onshapeDocuments, partsByCode]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="manufacturing-graph">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">BOM hierarchy</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BomTreeToolbar handleRef={handleRef} disabled={!tree} />
          {rootCandidates.length > 0 && (
            <Select
              size="small"
              value={effectiveRootBomId}
              onChange={(e) => setRootBomId(e.target.value)}
              displayEmpty
              sx={{ minWidth: 280 }}
            >
              {rootCandidates.map((b) => {
                const product = productById.get(b.product_id);
                const label =
                  product?.default_code ?? product?.name ?? b.code ?? b.product_id.slice(0, 8);
                return (
                  <MenuItem key={b.id} value={b.id}>
                    {label} ({b.bom_lines.length} lines)
                  </MenuItem>
                );
              })}
            </Select>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label="Assemblies-only view" variant="outlined" />
        <Chip
          size="small"
          label="Drag to pan • wheel to zoom • click chevron to expand"
          variant="outlined"
          color="primary"
        />
      </Stack>

      {loading && boms.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : !tree ? (
        <Typography variant="body2" color="text.secondary">
          No BOMs available. Run the manufacturing snapshot to populate.
        </Typography>
      ) : (
        <BomTreeCore<AssemblyNode>
          ref={handleRef}
          root={tree}
          childrenOf={childrenOf}
          keyOf={keyOf}
          renderNode={renderNode}
        />
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        Shows only sub-assemblies (products with their own BOM). The “N parts” badge counts
        non-assembly children. Click (i) to open the part in Onshape (login required); click PDF for
        the document's drawing and WI for its work-instruction drawing — both translated to PDF by
        the dashboard (no Onshape login needed). A greyed chip means none is available. Cycle/depth
        guard at {MAX_DEPTH}.
      </Typography>
    </Paper>
  );
}
